import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, filter, map, of, timeout } from 'rxjs';

import { featureFlagSelectors } from '../app-entities/feature-flag/feature-flag-state.selectors';
import { APP_ROUTES } from '../app-global/router/app-routes';
import { PeopleTabKey, selectVisiblePeopleTabs } from '../composite-selectors/people-tabs.selectors';

import { GUARD_TIMEOUT_MS } from './guard-timeout';

const homeRoute = `/${APP_ROUTES.App}/${APP_ROUTES.Home}`;

/**
 * Makes the Reviews subtree unreachable — list, detail, edit, form — when the company has switched
 * Quarterly Discussions off.
 *
 * Waits for flags, unlike the other People guards: this is where the People nav item lands by
 * default, so deciding immediately would flash the screen it exists to hide. Flag readiness implies
 * company settings have landed too.
 *
 * Redirects to the first visible People tab rather than home as web does, because the People toolbar
 * only renders on People pages — home would strand the section's other tabs.
 */
export const QuarterlyDiscussionsGuardFn: CanActivateFn = () => {
  const store = inject(Store);
  const router = inject(Router);

  return store.select(featureFlagSelectors.userFlagsAreLoaded).pipe(
    filter(flagsAreLoaded => !!flagsAreLoaded),
    concatLatestFrom(() => store.select(selectVisiblePeopleTabs)),
    map(([, visibleTabs]) => {
      if (visibleTabs.some(tab => tab.key === PeopleTabKey.Discussions)) return true;

      return router.parseUrl(visibleTabs[0]?.route ?? homeRoute);
    }),
    timeout(GUARD_TIMEOUT_MS),
    // Flags never arrived — fail open rather than strand the user, as the flag's own default does.
    catchError(() => of(true))
  );
};
